import { Link } from "react-router-dom";
import { ArrowUpRight, Lock, ShieldCheck, UserCircle2 } from "lucide-react";
import Reveal from "@/components/Reveal";

const HERO =
    "https://images.pexels.com/photos/30297108/pexels-photo-30297108.jpeg?auto=compress&cs=tinysrgb&w=2400";

export default function EspacePrive() {
    return (
        <div data-testid="espace-prive-page" className="bg-xv-black text-xv-ivory min-h-screen">
            <section className="relative pt-32 md:pt-44 pb-24 md:pb-32 overflow-hidden">
                <div className="absolute inset-0">
                    <img
                        src={HERO}
                        alt=""
                        className="w-full h-full object-cover opacity-25"
                    />
                    <div className="absolute inset-0 bg-gradient-to-b from-xv-black/85 via-xv-black/70 to-xv-black/95" />
                </div>
                <div className="relative max-w-[1100px] mx-auto px-6 md:px-12">
                    <Reveal>
                        <div className="flex items-center gap-4 mb-10 text-xv-goldSoft">
                            <span className="divider-gold" />
                            <span className="overline">
                                Accès confidentiel · Authentification requise
                            </span>
                        </div>
                    </Reveal>
                    <Reveal delay={0.1}>
                        <h1
                            className="font-serif text-xv-ivory text-[44px] sm:text-6xl md:text-7xl lg:text-[80px] leading-[0.98] tracking-tight max-w-4xl"
                            style={{ fontWeight: 300 }}
                        >
                            Espace privé{" "}
                            <em className="not-italic text-xv-goldSoft">
                                XV Capital Partners.
                            </em>
                        </h1>
                    </Reveal>
                    <Reveal delay={0.2}>
                        <p className="mt-12 max-w-2xl text-xv-ivory/70 text-[16px] md:text-[18px] leading-[1.85] font-light">
                            L'accès aux espaces privés du cabinet est strictement
                            réservé. Chaque connexion fait l'objet d'un
                            enregistrement horodaté. Sélectionnez l'espace qui
                            vous concerne.
                        </p>
                    </Reveal>
                </div>
            </section>

            <section className="relative pb-32 md:pb-40">
                <div className="max-w-[1100px] mx-auto px-6 md:px-12 grid md:grid-cols-2 gap-px bg-white/[0.06] border border-white/[0.06]">
                    {/* Admin / Collaborators */}
                    <Reveal>
                        <Link
                            to="/cockpit/login"
                            data-testid="private-access-cockpit"
                            className="group block bg-xv-black p-10 md:p-14 h-full transition-colors duration-700 hover:bg-xv-midnightDeep relative"
                        >
                            <div className="flex items-start justify-between mb-10">
                                <div className="overline text-xv-goldSoft/80">
                                    Cabinet · Cockpit
                                </div>
                                <ShieldCheck
                                    strokeWidth={1}
                                    size={28}
                                    className="text-xv-goldSoft/80"
                                />
                            </div>
                            <h2
                                className="font-serif text-[28px] md:text-[34px] text-xv-ivory leading-[1.05] mb-6"
                                style={{ fontWeight: 400 }}
                            >
                                Associés & collaborateurs
                            </h2>
                            <p className="text-xv-ivory/65 text-[14px] md:text-[15px] leading-[1.9] font-light max-w-md mb-12">
                                Cockpit interne de pilotage des opérations
                                confidentielles — dossiers, partenaires,
                                documents, notes. Accès strictement réservé
                                aux membres du cabinet.
                            </p>
                            <div className="inline-flex items-center gap-3 text-[11px] tracking-[0.3em] uppercase text-xv-goldSoft border-b border-xv-goldSoft/30 pb-1 group-hover:border-xv-goldSoft transition-colors duration-500">
                                Accéder au cockpit
                                <ArrowUpRight
                                    strokeWidth={1.2}
                                    size={14}
                                    className="transition-transform duration-500 group-hover:translate-x-1 group-hover:-translate-y-1"
                                />
                            </div>
                        </Link>
                    </Reveal>

                    {/* Clients */}
                    <Reveal delay={0.1}>
                        <Link
                            to="/client/login"
                            data-testid="private-access-clients"
                            className="group block bg-xv-black p-10 md:p-14 h-full transition-colors duration-700 hover:bg-xv-midnightDeep relative"
                        >
                            <div className="flex items-start justify-between mb-10">
                                <div className="overline text-xv-goldSoft/80">
                                    Clients · Suivi de dossier
                                </div>
                                <UserCircle2
                                    strokeWidth={1}
                                    size={28}
                                    className="text-xv-goldSoft/80"
                                />
                            </div>
                            <h2
                                className="font-serif text-[28px] md:text-[34px] text-xv-ivory leading-[1.05] mb-6"
                                style={{ fontWeight: 400 }}
                            >
                                Espace client confidentiel
                            </h2>
                            <p className="text-xv-ivory/65 text-[14px] md:text-[15px] leading-[1.9] font-light max-w-md mb-12">
                                Suivi en temps réel de votre dossier — étapes
                                réalisées, prochaines actions, documents
                                partagés, comptes rendus validés.
                                <br />
                                <span className="text-xv-ivory/55">
                                    Accès délivré nominativement par le cabinet, sur invitation.
                                </span>
                            </p>
                            <div className="inline-flex items-center gap-3 text-[11px] tracking-[0.3em] uppercase text-xv-goldSoft border-b border-xv-goldSoft/30 pb-1 group-hover:border-xv-goldSoft transition-colors duration-500">
                                <Lock strokeWidth={1.2} size={12} />
                                Accéder à mon espace
                                <ArrowUpRight
                                    strokeWidth={1.2}
                                    size={14}
                                    className="transition-transform duration-500 group-hover:translate-x-1 group-hover:-translate-y-1"
                                />
                            </div>
                            <p className="mt-10 text-[11px] text-xv-ivory/40 leading-[1.7]">
                                Vous êtes client du cabinet et avez reçu un
                                lien d'accès personnel ? Vous pouvez vous
                                connecter directement avec vos identifiants.
                            </p>
                        </Link>
                    </Reveal>
                </div>

                <div className="max-w-[1100px] mx-auto px-6 md:px-12 mt-16 text-center">
                    <p className="text-[10px] tracking-[0.32em] uppercase text-xv-ivory/35">
                        Sécurité · TOTP · Sessions horodatées · Journal d'audit
                    </p>
                </div>
            </section>
        </div>
    );
}
